/**
 * Service Detail Page with Subservices
 */
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Button,
  Chip,
  CircularProgress,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  TablePagination,
  Skeleton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Image as ImageIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/common';
import {
  useGetServiceQuery,
  useGetServiceSubServicesQuery,
  useGetInternalSubServicesQuery,
} from '../../store/api/servicesApi';
import { SubService } from '../../types/service';
import { formatCurrency } from '../../utils/pricing';
import moment from 'moment';

const ServiceDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [expandedSubservices, setExpandedSubservices] = useState<Set<number>>(new Set());
  const [subservicePage, setSubservicePage] = useState(0);
  const [subserviceRowsPerPage, setSubserviceRowsPerPage] = useState(10);

  const { data, isLoading, error } = useGetServiceQuery(Number(id));
  const { data: subservicesData, isLoading: isLoadingSubservices } = useGetServiceSubServicesQuery(
    {
      service_id: Number(id!),
      page: subservicePage + 1,
      page_size: subserviceRowsPerPage,
    },
    { skip: !id }
  );

  const service = data?.data;

  const handleToggleExpand = (subserviceId: number) => {
    setExpandedSubservices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(subserviceId)) {
        newSet.delete(subserviceId);
      } else {
        newSet.add(subserviceId);
      }
      return newSet;
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !service) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', py: 10 }}>
        <Typography color="error">{t('services.detail.notFound')}</Typography>
        <Button onClick={() => navigate('/services')} sx={{ mt: 2 }}>
          {t('services.buttons.backToServices')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={service.name}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.services'), path: '/services' },
          { label: service.name },
        ]}
      />

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/services')}
        sx={{ mb: 3 }}
      >
        {t('services.buttons.backToServices')}
      </Button>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: '2fr 1fr',
          },
          gap: 3,
        }}
      >
        {/* Main Content */}
        <Box>
          <Card sx={{ mb: 3, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                  <Typography variant="h5" fontWeight={600} gutterBottom>
                    {t('services.detail.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('services.detail.createdOn', {
                      date: moment(service.created_at).format('MMMM DD, YYYY'),
                    })}
                  </Typography>
                </Box>
                <Chip
                  label={service.is_active ? t('services.status.ACTIVE') : t('services.status.INACTIVE')}
                  color={service.is_active ? 'success' : 'default'}
                />
                {service.uses_dual_pricing && (
                  <Chip label={t('services.pricing.dualPricing')} color="info" sx={{ ml: 1 }} />
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                  },
                  gap: 3,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('services.fields.name')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {service.name}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('services.fields.nameAr')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500} dir="rtl">
                    {service.name_ar?.trim() ? service.name_ar : '—'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('services.fields.slug')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {service.slug}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('services.fields.order')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {service.order}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('services.fields.subservicesCount')}
                  </Typography>
                  <Chip label={service.subservices_count} color="primary" size="small" />
                </Box>

                <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('services.fields.description')}
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {service.description}
                  </Typography>
                </Box>

                <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('services.fields.descriptionAr')}
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }} dir="rtl">
                    {service.description_ar?.trim() ? service.description_ar : '—'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Sidebar */}
        <Box>
          <Card sx={{ mb: 3, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('services.serviceIcon')}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <Avatar
                  src={service.icon || undefined}
                  variant="rounded"
                  sx={{ width: 300, height: 300, maxWidth: '100%', maxHeight: '100%' }}
                >
                  <ImageIcon sx={{ fontSize: 150 }} />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Subservices Section - Full Width */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {t('services.detail.subservicesTitle', { count: subservicesData?.count || 0 })}
          </Typography>
          <Divider sx={{ my: 2 }} />

          {isLoadingSubservices ? (
            <Box>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 2, borderRadius: 1 }} />
              ))}
            </Box>
          ) : subservicesData?.results && subservicesData.results.length > 0 ? (
            <Box>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('services.cols.icon')}</TableCell>
                      <TableCell>{t('services.fields.name')}</TableCell>
                      <TableCell>{t('services.cols.price')}</TableCell>
                      <TableCell>{t('services.cols.status')}</TableCell>
                      <TableCell>{t('services.cols.internal')}</TableCell>
                      <TableCell align="right">{t('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subservicesData.results.map((subservice: SubService) => (
                      <React.Fragment key={subservice.id}>
                        <TableRow hover>
                          <TableCell>
                            <Avatar
                              src={subservice.icon || undefined}
                              variant="rounded"
                              sx={{ width: 40, height: 40 }}
                            >
                              <ImageIcon />
                            </Avatar>
                          </TableCell>
                          <TableCell>
                            <Box
                              onClick={() => navigate(`/subservices/${subservice.id}`)}
                              sx={{
                                cursor: 'pointer',
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                color="primary.main"
                              >
                                {subservice.name}
                              </Typography>
                            </Box>
                            {subservice.description && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {subservice.description.length > 50
                                  ? `${subservice.description.substring(0, 50)}...`
                                  : subservice.description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {subservice.uses_dual_pricing
                                ? `${formatCurrency(subservice.specialist_price)} / ${formatCurrency(subservice.consultant_price)}`
                                : formatCurrency(subservice.price)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={subservice.is_active ? t('services.status.ACTIVE') : t('services.status.INACTIVE')}
                              size="small"
                              color={subservice.is_active ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            {subservice.has_internal_subservices ? (
                              <Chip
                                label={t('services.detail.internalCount', { count: subservice.internal_subservices_count })}
                                size="small"
                                color="info"
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                {t('services.detail.none')}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {subservice.has_internal_subservices && (
                              <IconButton
                                size="small"
                                onClick={() => handleToggleExpand(subservice.id)}
                              >
                                {expandedSubservices.has(subservice.id) ? (
                                  <ExpandLessIcon />
                                ) : (
                                  <ExpandMoreIcon />
                                )}
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                        {subservice.has_internal_subservices && (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ py: 0, border: 0, px: 0 }}>
                              <Collapse
                                in={expandedSubservices.has(subservice.id)}
                                timeout="auto"
                                unmountOnExit
                              >
                                <Box sx={{ py: 1 }}>
                                  <InternalSubservicesList subserviceId={subservice.id} />
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {subservicesData.count > subserviceRowsPerPage && (
                <TablePagination
                  component="div"
                  count={subservicesData.count}
                  page={subservicePage}
                  onPageChange={(_, newPage) => setSubservicePage(newPage)}
                  rowsPerPage={subserviceRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setSubserviceRowsPerPage(parseInt(e.target.value, 10));
                    setSubservicePage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              )}
            </Box>
          ) : (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              {t('services.detail.noSubservices')}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

// Internal Subservices List Component
const InternalSubservicesList = ({ subserviceId }: { subserviceId: number }) => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetInternalSubServicesQuery({
    subservice_id: subserviceId,
    page: 1,
    page_size: 100,
  });

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rectangular" height={40} sx={{ mb: 1, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  if (!data?.results || data.results.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('services.detail.noInternalSubservices')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
        {t('services.detail.internalSubservices')}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('services.fields.name')}</TableCell>
              <TableCell>{t('services.cols.price')}</TableCell>
              <TableCell>{t('services.cols.status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.results.map((internal) => (
              <TableRow key={internal.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {internal.name}
                  </Typography>
                  {internal.description && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {internal.description.length > 40
                        ? `${internal.description.substring(0, 40)}...`
                        : internal.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {internal.uses_dual_pricing
                      ? `${formatCurrency(internal.specialist_price)} / ${formatCurrency(internal.consultant_price)}`
                      : formatCurrency(internal.price)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={internal.is_active ? t('services.status.ACTIVE') : t('services.status.INACTIVE')}
                    size="small"
                    color={internal.is_active ? 'success' : 'default'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ServiceDetailPage;
